import { Suspense, useEffect, useMemo, useState } from 'react'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { gql, useMutation, useQuery } from '@apollo/client'
import clsx from 'clsx'
import Error from '@/pages/_error'
import usePageView from '@/lib/tracking/usePageView'
import Scaffold from '@/components/Scaffold'
import BottomBar from '@/components/BottomBar'
import UiButton from '@/components/UiButton'
import TermsCheckModal from '@/components/TermsCheckModal'
import useTracking from '@/lib/tracking/useTracking'
import useScrollToHashAnchor from '@/hooks/useScrollToAnchor'
import { useAuth } from '@/hooks/useAuth'
import { useEnv } from '@/hooks/useEnv'
import useTrackingInfo from '@/hooks/useTrackingInfo'
import useCouponList from '@/hooks/useCouponList'
import useLiveInfo from '@/hooks/useLiveInfo'
import useTosV2 from '@/hooks/useTosV2'
import getGlcServiceName from '@/utils/getGlcServiceName'
import BagQuery from '@gql/BagQuery.gql'
import ShoppingBag from './components/ShoppingBag'
import Notice from './components/Notice'
import useCouponLink from './hooks/useCouponLink'
import SocialInfoContext, { useSocialInfoProvider } from './hooks/useSocialInfo'
import styles from './index.module.sass'
import useShopLandingLink from './hooks/useShopLandingLink'

export const PAGE_VISITED = gql`
  mutation visit($broadcastId: String!) {
    productListPageVisited(broadcastId: $broadcastId)
  }
`
const useNumOfProducts = (products) => useMemo(
  () => {
    const productList = products?.filter(product => product.display)
    return productList?.length ? ` (${productList.length})` : ''
  },
  [products]
)

export default function Bag () {
  const { t } = useTranslation()
  const { t: extT } = useTranslation('ext')
  const { tracker } = useTracking()

  const { broadcastId, serviceId, shopId, shopContext } = useLiveInfo()
  const { isAuthenticated } = useAuth()
  const { consentItems, isReady: isTosReady, isTosAgreed, sendAgreements } = useTosV2(serviceId)
  const { isLiffReady } = useEnv()
  const shouldSkip = !isLiffReady || !broadcastId

  const serviceName = useMemo(() => getGlcServiceName(serviceId, { t: extT }), [serviceId, extT])
  const { data: shopData, error: shopError } = shopContext

  const { data, error } = useQuery(BagQuery, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
    pollInterval: 60000,
    variables: { broadcastId, couponsOnly: false },
    skip: shouldSkip
  })

  const [refreshCoupons, { data: couponData }] = useCouponList(broadcastId)
  const couponLink = useCouponLink()
  const couponModule = useMemo(() => ({
    link: couponLink,
    coupons: couponData?.coupons,
    onRefresh: refreshCoupons,
    hasCoupons: data?.bag?.coupons?.length > 0
  }), [couponLink, couponData?.coupons, refreshCoupons, data])

  const numOfProducts = useNumOfProducts(data?.bag?.products)
  const socialInfo = useSocialInfoProvider(broadcastId, shouldSkip)

  const { screenName } = useTrackingInfo()
  usePageView({
    event: {
      screenName,
      params: {
        broadcast_id: broadcastId,
        shop_id: shopId
      }
    }
  }, !!shopId)

  useScrollToHashAnchor()

  const [pageVisited] = useMutation(PAGE_VISITED, {
    errorPolicy: 'ignore'
  })

  useEffect(() => {
    if (!isAuthenticated || !broadcastId) return
    pageVisited({
      variables: { broadcastId }
    })
  }, [isAuthenticated])

  const [isShowTosDialog, setShow] = useState(false)

  const initializing = (typeof data === 'undefined' && typeof error === 'undefined') || !isTosReady
  const isCouponListEmpty = initializing || !data?.bag?.coupons?.length

  useEffect(() => {
    if (!initializing && !isTosAgreed && !isCouponListEmpty) {
      setShow(true)
    }
    if (isTosAgreed) {
      refreshCoupons()
    }
  }, [initializing])

  useEffect(() => {
    if (isTosAgreed) {
      setShow(false)
    }
  }, [isTosAgreed])

  const shopLandingLink = useShopLandingLink(shopData?.shop?.landingUrl)

  const benefitEndTime = useMemo(() => {
    return data?.contractBenefit?.benefitEndTime
  }, [data])

  if (shopError) {
    return <Error />
  }

  const bottomBar = shopData?.shop?.landingUrl &&
    (
      <BottomBar>
        <div className={styles.bottomBarContent} >
          <UiButton
            linkUrl={shopLandingLink}
            text={t('client.shoplist.button.moreproducts')}
            onClick={() => {
              tracker?.event({
                config: { utsType: 'click' },
                event: {
                  screenName,
                  category: 'product_list',
                  params: {
                    target: 'see_more_products',
                    service_code: serviceId,
                    broadcast_id: broadcastId,
                    shop_id: shopId
                  }
                }
              })
            }}
          />
        </div>
      </BottomBar>
    )

  return (
    <>
      <Head>
        <title>{`${t('client.shoplist.title.prodlist')}${numOfProducts}`}</title>
      </Head>
      <Scaffold
        isPage
        bottomBar={bottomBar}
        className={clsx([styles.mainContnt, initializing ? styles.loading : undefined])}
      >
        <SocialInfoContext.Provider value={socialInfo}>
          <ShoppingBag
            benefitEndTime={benefitEndTime}
            isLoading={initializing}
            isTosAgreed={isTosAgreed}
            isShowTosDialog={isShowTosDialog}
            onCouponEmptyClick={() => setShow(true)}
            bag={data?.bag}
            coupon={couponModule}
          />
          {initializing ? <></> : <Notice shop={shopData?.shop} />}
        </SocialInfoContext.Provider>
      </Scaffold>
      {
        isShowTosDialog
          ? <Suspense fallback={null}>
              <TermsCheckModal
                serviceName={serviceName}
                consentItems={consentItems}
                onSubmit={(agreements) => {
                  sendAgreements(agreements)
                  setShow(false)
                }}
                onCancel={() => setShow(false)}
              />
            </Suspense>
          : <></>
      }
    </>
  )
}
